import channelService from '@/channel/services/ChannelService'
import dynamicCredentialsDataParser from '@/channel/utils/DynamicCredentialsDataParser'
import credentialsFieldHints from '@/channel/utils/CredentialsFieldHints'
import credentialsFormHelper from '@/channel/utils/CredentialsFormHelper'

/**
 * Estado comum das variações da tela de credenciais. O componente precisa ter
 * `channel` e `credentialsForm` (prop ou data) e um `saveCredentials` próprio,
 * que segue sendo quem grava.
 *
 * Fluxo do botão principal: valida obrigatórios -> testa -> grava. Se o teste
 * falhar, a tela mostra o motivo e oferece "Salvar mesmo assim" (loja fora do
 * ar, bloqueio 403 da hospedagem...).
 */
export default {
    data() {
        return {
            savingContent: false,
            testingConnection: false,
            ipBlockSuspected: false,
            // Resultado do último teste: { ok, message, at }
            lastTest: null,
            // Depois da primeira tentativa de salvar, campos vazios obrigatórios acusam erro
            submitted: false,
            savedSnapshot: '',
            // Estado do que está gravado (não do que está sendo digitado)
            savedConfigured: false,
        }
    },
    watch: {
        // Form novo (carga, troca de loja, novo usuário): vira a nova referência "salva"
        credentialsForm: {
            immediate: true,
            handler() {
                const self = this as any

                self.markSaved()
                self.lastTest = null
                self.submitted = false
                self.ipBlockSuspected = false
            },
        },
        credentialsStatus: {
            immediate: true,
            handler(status:string) {
                (this as any).$emit('state', status)
            },
        },
    },
    computed: {
        isDirty():boolean {
            const self = this as any

            return credentialsFormHelper.isDirty(self.credentialsForm, self.savedSnapshot)
        },
        isBusy():boolean {
            const self = this as any

            return self.savingContent || self.testingConnection
        },
        isConfigured():boolean {
            return (this as any).savedConfigured
        },
        // 'idle' | 'connecting' | 'connected' (ChannelConnectionDiagramCmp)
        diagramState():string {
            const self = this as any

            if (self.testingConnection) {
                return 'connecting'
            }

            if (self.lastTest) {
                return self.lastTest.ok ? 'connected' : 'idle'
            }

            return self.isConfigured && !self.isDirty ? 'connected' : 'idle'
        },
        // Selo do cabeçalho: 'testing' | 'ok' | 'failed' | 'configured' | 'unconfigured'
        credentialsStatus():string {
            const self = this as any

            if (self.testingConnection) {
                return 'testing'
            }

            if (self.lastTest) {
                return self.lastTest.ok ? 'ok' : 'failed'
            }

            return self.isConfigured ? 'configured' : 'unconfigured'
        },
    },
    methods: {
        markSaved() {
            const self = this as any

            self.savedSnapshot = credentialsFormHelper.snapshotValues(self.credentialsForm)
            self.savedConfigured = credentialsFormHelper.isConfigured(self.credentialsForm)
        },
        focusField(key:string) {
            const refs = (this as any).$refs[`field-${key}`]
            const field = Array.isArray(refs) ? refs[0] : refs

            if (field) {
                field.focus()
            }
        },
        focusFirstField() {
            const keys = Object.keys((this as any).credentialsForm || {})

            if (keys.length) {
                (this as any).focusField(keys[0])
            }
        },
        // true quando o canal respondeu
        async runTest(credentialsForm:any):Promise<boolean> {
            const self = this as any

            self.$floatingAlert.hide()
            self.testingConnection = true

            const credentials = dynamicCredentialsDataParser.mountCredentialsObjectFromForm(credentialsForm)
            let ok = false

            try {
                await channelService.pingChannel(self.channel.enumChannel, credentials)

                ok = true
                self.ipBlockSuspected = false
                self.lastTest = { ok: true, message: '', at: formatNow() }
            } catch (e) {
                const error = e as any

                self.ipBlockSuspected = self.channel.isWooCommerce() && credentialsFieldHints.isIpBlockError(error.message)
                self.lastTest = {
                    ok: false,
                    message: error.message || 'O canal não respondeu. Verifique as credenciais informadas e tente novamente.',
                    at: formatNow(),
                }
            }

            self.testingConnection = false

            return ok
        },
        async testAndSave(credentialsForm:any, persist:() => Promise<void>) {
            const self = this as any

            self.submitted = true

            const missing = credentialsFormHelper.getMissingRequiredFields(credentialsForm)

            if (missing.length) {
                self.$nextTick(() => self.focusField(missing[0]))
                return
            }

            if (await self.runTest(credentialsForm)) {
                await persist()
                return
            }

            // Leva o leitor de tela (e o olho) para o motivo da falha. No 403 do
            // WooCommerce quem assume o foco é o aviso da hospedagem.
            if (!self.ipBlockSuspected) {
                self.$nextTick(() => self.$refs.testResult && self.$refs.testResult.focus())
            }
        },
    },
}

function formatNow():string {
    return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}
